import { useContext, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { alpha } from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PercentIcon from '@mui/icons-material/Percent';
import axiosInstance from '../../lib/axios';
import { getUser } from '../../utils/auth';
import { palette, typography, theme } from '../../theme/colors';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { useCanViewFinancials } from '../../hooks/usePermissions';

interface ProfitByPeriodResponse {
  period: {
    startDate: string;
    endDate: string;
    type: string;
  };
  data: Array<{
    period: string;
    revenue: number;
    cogs: number;
    profit: number;
    marginPercent: number;
    salesCount: number;
  }>;
}

interface ExpenseByPeriodItem {
  period: string;
  totalAmount: number;
  count: number;
}

interface ExpenseByCategoryItem {
  category: string;
  totalAmount: number;
  count: number;
  percentage: number;
}

interface ExpenseSummary {
  totalAmount: number;
  expenseCount: number;
  averageAmount: number;
  byCategory: Record<string, number>;
  byPaymentMethod: Record<string, number>;
}

const CATEGORY_COLORS: Record<string, string> = {
  restock: '#3B82F6',
  salary: '#10B981',
  utilities: '#F59E0B',
  rent: '#8B5CF6',
  maintenance: '#EC4899',
  consumable: '#06B6D4',
  transport: '#F97316',
  other: '#6B7280',
};

const CATEGORY_LABELS: Record<string, string> = {
  restock: 'Restock',
  salary: 'Salaries',
  utilities: 'Utilities',
  rent: 'Rent',
  maintenance: 'Maintenance',
  consumable: 'Consumables',
  transport: 'Transport',
  other: 'Other',
};

const fetchProfitByPeriod = async (
  startDate: Date,
  endDate: Date,
  periodType: string
): Promise<ProfitByPeriodResponse> => {
  const user = getUser();
  const params: Record<string, any> = {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
    periodType,
  };

  if (user?.locationId) {
    params.locationId = user.locationId;
  }

  const response = await axiosInstance.get('/sales/analytics/profit/by-period', { params });
  return response.data;
};

const fetchExpensesByPeriod = async (
  startDate: Date,
  endDate: Date,
  periodType: string
): Promise<ExpenseByPeriodItem[]> => {
  const user = getUser();
  const params: Record<string, any> = {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
    periodType,
  };

  if (user?.locationId) {
    params.locationId = user.locationId;
  }

  const response = await axiosInstance.get('/expenses/analytics/by-period', { params });
  return response.data;
};

const fetchExpensesByCategory = async (
  startDate: Date,
  endDate: Date
): Promise<ExpenseByCategoryItem[]> => {
  const user = getUser();
  const params: Record<string, any> = {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };

  if (user?.locationId) {
    params.locationId = user.locationId;
  }

  const response = await axiosInstance.get('/expenses/analytics/by-category', { params });
  return response.data;
};

const fetchExpenseSummary = async (
  startDate: Date,
  endDate: Date
): Promise<ExpenseSummary> => {
  const user = getUser();
  const params: Record<string, any> = {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };

  if (user?.locationId) {
    params.locationId = user.locationId;
  }

  const response = await axiosInstance.get('/expenses/analytics/summary', { params });
  return response.data;
};

export const SalesExpensesDashboard = () => {
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();
  const canViewFinancials = useCanViewFinancials();

  // Date range state - default to last 30 days
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [startDate, setStartDate] = useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });
  const [selectedPreset, setSelectedPreset] = useState<string>('30d');
  const [periodType, setPeriodType] = useState<'day' | 'week' | 'month'>('day');

  const { data: profitData, isLoading: loadingProfit, error: profitError } = useQuery({
    queryKey: ['salesExpenses-profit', startDate, endDate, periodType],
    queryFn: () => fetchProfitByPeriod(startDate, endDate, periodType),
  });

  const { data: expensesByPeriod, isLoading: loadingExpensesPeriod, error: expensesPeriodError } = useQuery({
    queryKey: ['salesExpenses-expensesByPeriod', startDate, endDate, periodType],
    queryFn: () => fetchExpensesByPeriod(startDate, endDate, periodType),
  });

  const { data: expensesByCategory, isLoading: loadingExpensesCategory, error: expensesCategoryError } = useQuery({
    queryKey: ['salesExpenses-expensesByCategory', startDate, endDate],
    queryFn: () => fetchExpensesByCategory(startDate, endDate),
  });

  const { data: expenseSummary, isLoading: loadingExpenseSummary, error: expenseSummaryError } = useQuery({
    queryKey: ['salesExpenses-expenseSummary', startDate, endDate],
    queryFn: () => fetchExpenseSummary(startDate, endDate),
  });

  // Merge earnings and expenses data for combined chart
  const combinedChartData = useMemo(() => {
    if (!profitData?.data || !expensesByPeriod) return [];

    const dataMap = new Map<string, {
      period: string;
      revenue: number;
      expenses: number;
      netProfit: number;
    }>();

    // Add profit data
    profitData.data.forEach((item) => {
      dataMap.set(item.period, {
        period: item.period,
        revenue: item.revenue || 0,
        expenses: 0,
        netProfit: 0,
      });
    });

    // Add expenses data
    expensesByPeriod.forEach((item) => {
      const existing = dataMap.get(item.period);
      if (existing) {
        existing.expenses = item.totalAmount || 0;
        existing.netProfit = existing.revenue - existing.expenses;
      } else {
        dataMap.set(item.period, {
          period: item.period,
          revenue: 0,
          expenses: item.totalAmount || 0,
          netProfit: -(item.totalAmount || 0),
        });
      }
    });

    return Array.from(dataMap.values()).sort((a, b) =>
      a.period.localeCompare(b.period)
    );
  }, [profitData, expensesByPeriod]);

  // Calculate totals
  const totals = useMemo(() => {
    const totalRevenue = profitData?.data?.reduce((sum, item) => sum + (item.revenue || 0), 0) || 0;
    const totalExpenses = expenseSummary?.totalAmount || 0;
    const netProfit = totalRevenue - totalExpenses;
    const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;

    return {
      totalRevenue,
      totalExpenses,
      netProfit,
      profitMargin,
    };
  }, [profitData, expenseSummary]);

  const handlePresetClick = (preset: string) => {
    const end = new Date();
    const start = new Date();

    switch (preset) {
      case '7d':
        start.setDate(start.getDate() - 7);
        break;
      case '30d':
        start.setDate(start.getDate() - 30);
        break;
      case '90d':
        start.setDate(start.getDate() - 90);
        break;
      case '1y':
        start.setFullYear(start.getFullYear() - 1);
        break;
    }

    setStartDate(start);
    setEndDate(end);
    setSelectedPreset(preset);
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(new Date(e.target.value));
    setSelectedPreset('custom');
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(new Date(e.target.value));
    setSelectedPreset('custom');
  };

  const colors = mode === 'dark'
    ? {
        bg: palette.background,
        surface: palette.surface,
        surfaceElevated: palette.surfaceElevated,
        border: palette.border,
        borderLight: palette.borderLight,
        textPrimary: palette.textPrimary,
        textSecondary: palette.textSecondary,
        textMuted: palette.textMuted,
      }
    : {
        bg: palette.light.background,
        surface: palette.light.surface,
        surfaceElevated: palette.light.surfaceElevated,
        border: palette.light.border,
        borderLight: palette.light.borderLight,
        textPrimary: palette.light.textPrimary,
        textSecondary: palette.light.textSecondary,
        textMuted: palette.light.textMuted,
      };

  const cardStyles = {
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: theme.radius.xl,
    p: 3,
    transition: theme.transitions.normal,
  };

  const formatCurrency = (amount: number) => {
    if (!canViewFinancials) return '***';
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-DZ', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateForInput = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const getDaysRange = () => {
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const isLoading = loadingProfit || loadingExpensesPeriod || loadingExpensesCategory || loadingExpenseSummary;
  const error = profitError || expensesPeriodError || expensesCategoryError || expenseSummaryError;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: palette.primary }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert
          severity="error"
          sx={{
            borderRadius: theme.radius.lg,
            backgroundColor: palette.errorMuted,
            color: palette.error,
          }}
        >
          {error instanceof Error ? error.message : 'Failed to load analytics data'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Page Header */}
      <Box sx={{ mb: 3, ...animations.slideDown }}>
        <Typography
          variant="overline"
          sx={{
            color: palette.primary,
            fontFamily: typography.fontFamily.mono,
            fontSize: typography.fontSize.xs,
            letterSpacing: typography.letterSpacing.widest,
            display: 'block',
            mb: 0.5,
          }}
        >
          Financial Overview
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.bold,
            color: colors.textPrimary,
            mb: 0.5,
          }}
        >
          Sales & Expenses
        </Typography>
        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
          {getDaysRange()} days of earnings vs spending trends
        </Typography>
      </Box>

      {/* Date Range Picker & Period Type */}
      <Box
        sx={{
          ...cardStyles,
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: { xs: 'stretch', md: 'center' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarTodayOutlinedIcon sx={{ color: colors.textMuted, fontSize: 20 }} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: typography.fontWeight.semibold,
              color: colors.textPrimary,
            }}
          >
            Time Period
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flex: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <ButtonGroup
            variant="outlined"
            sx={{
              '& .MuiButton-root': {
                borderColor: colors.border,
                color: colors.textSecondary,
                textTransform: 'none',
                fontWeight: typography.fontWeight.medium,
                fontSize: typography.fontSize.sm,
                '&:hover': {
                  borderColor: palette.primary,
                  backgroundColor: palette.primaryMuted,
                },
              },
            }}
          >
            {['7d', '30d', '90d', '1y'].map((preset) => (
              <Button
                key={preset}
                onClick={() => handlePresetClick(preset)}
                sx={{
                  ...(selectedPreset === preset && {
                    backgroundColor: palette.primaryMuted,
                    borderColor: `${palette.primary} !important`,
                    color: palette.primary,
                  }),
                }}
              >
                {preset === '7d' && 'Last 7 days'}
                {preset === '30d' && 'Last 30 days'}
                {preset === '90d' && 'Last 90 days'}
                {preset === '1y' && 'Last year'}
              </Button>
            ))}
          </ButtonGroup>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField
              type="date"
              label="Start Date"
              value={formatDateForInput(startDate)}
              onChange={handleStartDateChange}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.surfaceElevated,
                  '& fieldset': { borderColor: colors.border },
                  '&:hover fieldset': { borderColor: colors.borderLight },
                  '&.Mui-focused fieldset': { borderColor: palette.primary },
                },
                '& .MuiInputLabel-root': { color: colors.textMuted },
                '& .MuiInputBase-input': { color: colors.textPrimary },
              }}
            />
            <Typography variant="body2" sx={{ color: colors.textMuted }}>
              to
            </Typography>
            <TextField
              type="date"
              label="End Date"
              value={formatDateForInput(endDate)}
              onChange={handleEndDateChange}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.surfaceElevated,
                  '& fieldset': { borderColor: colors.border },
                  '&:hover fieldset': { borderColor: colors.borderLight },
                  '&.Mui-focused fieldset': { borderColor: palette.primary },
                },
                '& .MuiInputLabel-root': { color: colors.textMuted },
                '& .MuiInputBase-input': { color: colors.textPrimary },
              }}
            />
          </Box>

          <ToggleButtonGroup
            value={periodType}
            exclusive
            onChange={(_e, value) => value && setPeriodType(value)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                borderColor: colors.border,
                color: colors.textSecondary,
                textTransform: 'none',
                fontWeight: typography.fontWeight.medium,
                fontSize: typography.fontSize.xs,
                '&.Mui-selected': {
                  backgroundColor: palette.primaryMuted,
                  borderColor: palette.primary,
                  color: palette.primary,
                  '&:hover': {
                    backgroundColor: palette.primaryMuted,
                  },
                },
              },
            }}
          >
            <ToggleButton value="day">Daily</ToggleButton>
            <ToggleButton value="week">Weekly</ToggleButton>
            <ToggleButton value="month">Monthly</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Card
            sx={{
              ...cardStyles,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: palette.primary,
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.textMuted,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    letterSpacing: typography.letterSpacing.widest,
                  }}
                >
                  Total Revenue
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.extrabold,
                    color: colors.textPrimary,
                    mt: 1,
                  }}
                >
                  {formatCurrency(totals.totalRevenue)}
                </Typography>
              </Box>
              <ReceiptLongIcon sx={{ fontSize: 40, color: alpha(palette.primary, 0.2) }} />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card
            sx={{
              ...cardStyles,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: palette.error,
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.textMuted,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    letterSpacing: typography.letterSpacing.widest,
                  }}
                >
                  Total Expenses
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.extrabold,
                    color: palette.error,
                    mt: 1,
                  }}
                >
                  {formatCurrency(totals.totalExpenses)}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  {expenseSummary?.expenseCount || 0} expenses
                </Typography>
              </Box>
              <AccountBalanceWalletIcon sx={{ fontSize: 40, color: alpha(palette.error, 0.2) }} />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card
            sx={{
              ...cardStyles,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: totals.netProfit >= 0 ? palette.success : palette.error,
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.textMuted,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    letterSpacing: typography.letterSpacing.widest,
                  }}
                >
                  Net Profit
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.extrabold,
                    color: totals.netProfit >= 0 ? palette.success : palette.error,
                    mt: 1,
                  }}
                >
                  {totals.netProfit > 0 ? '+' : ''}
                  {formatCurrency(totals.netProfit)}
                </Typography>
              </Box>
              {totals.netProfit >= 0 ? (
                <TrendingUpIcon sx={{ fontSize: 40, color: alpha(palette.success, 0.2) }} />
              ) : (
                <TrendingDownIcon sx={{ fontSize: 40, color: alpha(palette.error, 0.2) }} />
              )}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card
            sx={{
              ...cardStyles,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: palette.info,
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.textMuted,
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    letterSpacing: typography.letterSpacing.widest,
                  }}
                >
                  Profit Margin
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.extrabold,
                    color: totals.profitMargin >= 0 ? palette.info : palette.error,
                    mt: 1,
                  }}
                >
                  {canViewFinancials ? `${totals.profitMargin.toFixed(1)}%` : '***'}
                </Typography>
              </Box>
              <PercentIcon sx={{ fontSize: 40, color: alpha(palette.info, 0.2) }} />
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Main Chart - Earnings vs Expenses */}
      <Box sx={{ ...cardStyles, mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.semibold,
            color: colors.textPrimary,
            mb: 3,
          }}
        >
          Earnings vs Expenses Trend
        </Typography>
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart
            data={combinedChartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorRevenueSE" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={palette.primary} stopOpacity={0.3} />
                <stop offset="95%" stopColor={palette.primary} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorExpensesSE" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={palette.error} stopOpacity={0.3} />
                <stop offset="95%" stopColor={palette.error} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorNetProfitSE" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={palette.success} stopOpacity={0.3} />
                <stop offset="95%" stopColor={palette.success} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
            <XAxis
              dataKey="period"
              stroke={colors.textMuted}
              tickFormatter={formatDate}
              style={{ fontSize: typography.fontSize.xs }}
            />
            <YAxis stroke={colors.textMuted} style={{ fontSize: typography.fontSize.xs }} />
            <Tooltip
              contentStyle={{
                backgroundColor: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: theme.radius.md,
                color: colors.textPrimary,
              }}
              labelStyle={{ color: colors.textPrimary }}
              formatter={(value: any, name: string) => {
                if (!canViewFinancials) return ['***', name];
                const formattedValue = new Intl.NumberFormat('fr-DZ', {
                  style: 'currency',
                  currency: 'DZD',
                }).format(value);
                if (name === 'revenue') return [formattedValue, 'Revenue'];
                if (name === 'expenses') return [formattedValue, 'Expenses'];
                if (name === 'netProfit') return [formattedValue, 'Net Profit'];
                return [formattedValue, name];
              }}
              labelFormatter={(label) => formatDate(label)}
            />
            <Legend
              wrapperStyle={{ color: colors.textPrimary }}
              formatter={(value) => {
                if (value === 'revenue') return 'Revenue';
                if (value === 'expenses') return 'Expenses';
                if (value === 'netProfit') return 'Net Profit';
                return value;
              }}
            />
            <Area
              type="monotone"
              dataKey="revenue"
              stroke={palette.primary}
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorRevenueSE)"
            />
            <Area
              type="monotone"
              dataKey="expenses"
              stroke={palette.error}
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorExpensesSE)"
            />
            <Area
              type="monotone"
              dataKey="netProfit"
              stroke={palette.success}
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorNetProfitSE)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>

      {/* Bottom Row - Pie Chart & Category Table */}
      <Grid container spacing={3}>
        {/* Expense Breakdown Pie Chart */}
        <Grid item xs={12} lg={5}>
          <Box sx={cardStyles}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.semibold,
                color: colors.textPrimary,
                mb: 3,
              }}
            >
              Expense Breakdown
            </Typography>
            {expensesByCategory && expensesByCategory.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={expensesByCategory}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="totalAmount"
                      nameKey="category"
                    >
                      {expensesByCategory.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={CATEGORY_COLORS[entry.category] || CATEGORY_COLORS.other}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: colors.surface,
                        border: `1px solid ${colors.border}`,
                        borderRadius: theme.radius.md,
                        color: colors.textPrimary,
                      }}
                      formatter={(value: any, name: string) => [
                        canViewFinancials
                          ? new Intl.NumberFormat('fr-DZ', { style: 'currency', currency: 'DZD' }).format(value)
                          : '***',
                        CATEGORY_LABELS[name] || name,
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{ mt: 2 }}>
                  {expensesByCategory.slice(0, 6).map((item) => (
                    <Box
                      key={item.category}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: CATEGORY_COLORS[item.category] || CATEGORY_COLORS.other,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.textSecondary,
                          flex: 1,
                        }}
                      >
                        {CATEGORY_LABELS[item.category] || item.category}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.textPrimary,
                          fontFamily: typography.fontFamily.mono,
                        }}
                      >
                        {formatCurrency(item.totalAmount)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.textMuted,
                          fontFamily: typography.fontFamily.mono,
                          minWidth: 45,
                          textAlign: 'right',
                        }}
                      >
                        {item.percentage.toFixed(1)}%
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 250,
                }}
              >
                <Typography variant="body2" sx={{ color: colors.textMuted }}>
                  No expense data available
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>

        {/* Expense Category Table */}
        <Grid item xs={12} lg={7}>
          <Box sx={cardStyles}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.semibold,
                color: colors.textPrimary,
                mb: 3,
              }}
            >
              Expense Details by Category
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ color: colors.textMuted, fontWeight: typography.fontWeight.semibold }}
                    >
                      Category
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: colors.textMuted, fontWeight: typography.fontWeight.semibold }}
                    >
                      Count
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: colors.textMuted, fontWeight: typography.fontWeight.semibold }}
                    >
                      Total Amount
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: colors.textMuted, fontWeight: typography.fontWeight.semibold }}
                    >
                      % of Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expensesByCategory && expensesByCategory.length > 0 ? (
                    expensesByCategory.map((item) => (
                      <TableRow
                        key={item.category}
                        sx={{
                          '&:hover': { backgroundColor: colors.surfaceElevated },
                          transition: theme.transitions.fast,
                        }}
                      >
                        <TableCell sx={{ color: colors.textPrimary }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: CATEGORY_COLORS[item.category] || CATEGORY_COLORS.other,
                              }}
                            />
                            {CATEGORY_LABELS[item.category] || item.category}
                          </Box>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: colors.textPrimary, fontFamily: typography.fontFamily.mono }}
                        >
                          {item.count}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: palette.error,
                            fontFamily: typography.fontFamily.mono,
                            fontWeight: typography.fontWeight.semibold,
                          }}
                        >
                          {formatCurrency(item.totalAmount)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: colors.textSecondary, fontFamily: typography.fontFamily.mono }}
                        >
                          {item.percentage.toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4, color: colors.textMuted }}>
                        No expense data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
